<template>
  <!--suppress JSValidateTypes -->
  <hc-search
    :name="filter.name"
    :metro="filter.metro"
    :deadline="filter.deadline"
    :how="filter.how"
    :minCostApartment="filter.minCostApartment"
    :maxCostApartment="filter.maxCostApartment"
    :minCostSquareMeter="filter.minCostSquareMeter"
    :maxCostSquareMeter="filter.maxCostSquareMeter"
    :available="filter.available"
    @newSearch="updateGetHc"
  />
  <!--suppress JSUnresolvedVariable -->
  <div v-for="item in hc.housingComplexes" :key="item.id">
    <hc_card :hc="item"/>
  </div>
  <pagination
    :number="10"
    :page="filter.page"
    :total="hc.total"
    @newPagination="updatePage"
  />
</template>

<script>
import useHc from "@/hooks/useHc";
import {onMounted, ref, computed} from "vue";
import hc_card from "@/components/pages/hc/HcCard";
import HcSearch from "@/components/pages/hc/HcSearch";
import Pagination from "@/components/UI/Pagination";
import {useRoute, useRouter} from "vue-router";
import {useHead} from "@unhead/vue";

export default {
  name: "housing-complexes",
  components: {Pagination, HcSearch, hc_card},
  setup() {
    const route = useRoute()
    const router = useRouter()
    const {getHc, hc} = useHc()

    // Динамические мета-теги
    const pageTitle = computed(() => {
      const selectedCity = localStorage.getItem('selectedCity');
      let base = "p-l-e.ru — Продажа и поиск недвижимости | Жилые комплексы";

      if (selectedCity === "Вся Россия") {
        base += " по всей России";
      } else if (selectedCity && selectedCity !== "null") {
        base += ` в г. ${selectedCity}`;
      }

      return base;
    });

    const pageDescription = computed(() => {
      return "Каталог жилых комплексов России. Актуальные цены, планировки квартир, рейтинги застройщиков, сроки сдачи новостроек."
    })

    const canonicalUrl = computed(() => {
      const baseUrl = 'https://p-l-e.ru/hc'
      const queryParams = new URLSearchParams()

      if (route.query.name) queryParams.append('name', route.query.name)
      if (route.query.metro) queryParams.append('metro', route.query.metro)
      if (route.query.page && route.query.page > 1) queryParams.append('page', route.query.page)

      return queryParams.toString() ? `${baseUrl}?${queryParams.toString()}` : baseUrl
    })

    useHead({
      title: pageTitle,
      meta: [
        { name: 'description', content: pageDescription },
        { name: 'keywords', content: computed(() =>
            `жилые комплексы, новостройки , купить квартиру, рейтинг ЖК`
          )},

        // OpenGraph
        { property: 'og:title', content: pageTitle },
        { property: 'og:description', content: pageDescription },
        { property: 'og:url', content: canonicalUrl },
      ],
      link: [
        { rel: 'canonical', href: canonicalUrl },
        ...(route.query.page > 1 ? [
          { rel: 'prev', href: computed(() => {
              const prevPage = Number(route.query.page) - 1
              return prevPage > 1 ? `${canonicalUrl.value.split('?')[0]}?page=${prevPage}` : canonicalUrl.value.split('?')[0]
            })}
        ] : []),
        ...(hc.value.total > 10 * route.query.page ? [
          { rel: 'next', href: computed(() => `${canonicalUrl.value.split('?')[0]}?page=${Number(route.query.page) + 1}`)}
        ] : [])
      ],
      script: [
        {
          type: 'application/ld+json',
          innerHTML: computed(() => JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "itemListElement": hc.value.housingComplexes?.map((item, index) => ({
              "@type": "ListItem",
              "position": index + 1,
              "item": {
                "@type": "HousingComplex",
                "name": item.name,
                "url": `https://p-l-e.ru/hc/${item.id}`,
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": item.address
                },
                "image": item.main_photo,
                "description": item.short_description
              }
            }))
          }))
        }
      ]
    })

    onMounted(async () => {
      await router.replace({
        query: myQuery()
      })
      await getHc()
    })

    const filter = ref({
      name: route.query.name ? route.query.name : "",
      metro: route.query.metro ? route.query.metro : "",
      deadline: route.query.deadline ? route.query.deadline : "",
      how: route.query.how ? route.query.how : "",
      minCostApartment: route.query.min_ca ? route.query.min_ca : "",
      maxCostApartment: route.query.max_ca ? route.query.max_ca : "",
      minCostSquareMeter: route.query.min_csm ? route.query.min_csm : "",
      maxCostSquareMeter: route.query.max_csm ? route.query.max_csm : "",
      available: route.query.available ? route.query.available : "true",
      page: Number(route.query.page) ? Number(route.query.page) : 1
    })

    const myQuery = () => {
      const query = {
        name: filter.value.name,
        metro: filter.value.metro,
        deadline: filter.value.deadline,
        how: filter.value.how,
        min_ca: filter.value.minCostApartment,
        max_ca: filter.value.maxCostApartment,
        min_csm: filter.value.minCostSquareMeter,
        max_csm: filter.value.maxCostSquareMeter,
        available: filter.value.available,
        page: filter.value.page
      }

      // Удаляем пустые параметры
      Object.keys(query).forEach(key => {
        if (query[key] === "" || query[key] === "true" || (key === 'page' && query[key] === 1)) {
          delete query[key]
        }
      })

      return query
    }

    const updatePage = async (page) => {
      filter.value.page = page
      await router.replace({
        query: myQuery()
      })
      location.reload()
    }

    const updateGetHc = async (
      name,
      metro,
      deadline,
      how,
      minCostApartment,
      maxCostApartment,
      minCostSquareMeter,
      maxCostSquareMeter,
      available
    ) => {
      filter.value.name = name
      filter.value.metro = metro
      filter.value.deadline = deadline
      filter.value.how = how
      filter.value.minCostApartment = minCostApartment
      filter.value.maxCostApartment = maxCostApartment
      filter.value.minCostSquareMeter = minCostSquareMeter
      filter.value.maxCostSquareMeter = maxCostSquareMeter
      filter.value.available = available
      filter.value.page = 1
      await router.replace({
        query: myQuery()
      })
      location.reload()
    }

    return {updateGetHc, updatePage, hc, filter}
  }
}
</script>

<style scoped>

</style>